<div
  *ngIf="options?.headerConfig?.displayHeader ?? true"
  class="header"
  appStickyTableHeader
  [tableContainer]="tableContainer"
  [isEnabled]="options?.headerConfig?.enableStickyDirective ?? true"
  [rootMargin]="options?.headerConfig?.rootMargin ?? defaultRootMargin"
>
  <div class="entriesCount">
    <span
      *ngIf="dataSource.filteredData && options?.labeling?.entityName && options?.labeling?.entityNamePlural"
      [ngPlural]="(dataSource.filteredData | filterByProperty : 'level').length ?? 0"
    >
      {{(dataSource.filteredData | filterByProperty : 'level')?.length ?? 0}}
      <ng-template ngPluralCase="=1">{{options?.labeling.entityName ?? 'row'}}</ng-template>
      <ng-template ngPluralCase="other">{{options?.labeling.entityNamePlural ?? 'rows'}}</ng-template>
    </span>
  </div>

  <div *ngIf="options?.sortConfig?.mode === GridSortMode.Selection" class="tableHeaderSortContainer">
    <ng-container *ngTemplateOutlet="options?.sortConfig?.selectionTemplate ?? sortSelectionTemplate"></ng-container>
  </div>

  <div class="header-buttons">
    <!-- save button -->
    <ng-container *ngIf="!options?.saveConfig?.isExternalSave && (changes.hasValue$ | async)">
      <button mat-flat-button color="primary" (click)="onSave()">
        <mat-icon [svgIcon]="options?.icons?.saveButton ?? 'content-save'"></mat-icon>
        {{options?.labeling?.saveButton ?? 'Save changes'}}
      </button>
    </ng-container>

    <!-- copy button -->
    <div *ngIf="options?.copyConfig?.allowCopying && selection.selected.length">
      <ng-container *ngTemplateOutlet="options?.copyConfig?.copyRowsTemplate ?? copyRowsTemplate"></ng-container>
    </div>

    <!-- delete button -->
    <ng-container
      *ngIf="options?.deleteConfig?.allowDeleting && !options?.deleteConfig?.isExternalDelete && (selection.selected | keyvalue).length as selectedRowsCount"
    >
      <button
        data-testId="bulk-delete"
        mat-flat-button
        color="warn"
        class="mat-secondary-danger"
        (click)="onDeleteSelectedRows()"
      >
        <mat-icon
          [ngClass]="{'mr-0': options?.labeling?.hideLabel}"
          [svgIcon]="options?.icons?.deleteButton ?? 'close'"
        ></mat-icon>
        <span *ngIf="!options?.labeling?.hideLabel">
          {{options?.labeling?.deleteButton ?? 'Delete'}} ({{selectedRowsCount}})
        </span>
      </button>
    </ng-container>

    <!-- import button -->
    <div *ngIf="options?.importConfig?.allowImporting">
      <ng-container *ngTemplateOutlet="options?.importConfig?.importTemplate"></ng-container>
    </div>

    <!-- add button -->
    <ng-container *ngIf="options?.addConfig?.allowAdding">
      <ng-container
        *ngTemplateOutlet="options?.addConfig.buttonTemplate ?
          options?.addConfig.buttonTemplate :
          (options?.addConfig.buttonPosition === 'header' ? headerAddButtonTemplate : floatingAddButtonTemplate)"
      ></ng-container>
    </ng-container>
  </div>
</div>

<div #tableContainer class="tableContainer" [ngClass]="{entityLabel : options?.labeling?.entityLabel }">
  <!-- Necessary for sticky header to avoid flickering -->
  <div class="rowPlaceholder"></div>

  <table
    #table
    mat-table
    [dataSource]="dataSource"
    [trackBy]="trackByRow"
    matSort
    [matSortDisabled]="options?.sortConfig?.mode !== GridSortMode.ColumnHeader || !(options?.sortConfig?.allowSorting ?? true)"
    (matSortChange)="options?.events?.onColumnHeaderSorting && options?.events?.onColumnHeaderSorting()"
    class="dragBoundary"
    cdkDropList
    [cdkDropListData]="dataSource.data"
    [cdkDropListDisabled]="!(options?.dragAndDropConfig?.allowDragAndDrop ?? false) || dragDisabled"
    (cdkDropListDropped)="onRowDragged($event)"
  >
    <!-- Drag and Drop column -->
    <ng-container matColumnDef="dragAndDrop">
      <th mat-header-cell *matHeaderCellDef class="qa-grid-drag-and-drop-column-header"></th>
      <td mat-cell *matCellDef="let element" class="align-center qa-grid-column-drag-and-drop">
        <mat-icon svgIcon="drag-indicator" class="dragHandle" (mousedown)="dragDisabled = false"></mat-icon>
      </td>
      <td mat-footer-cell *matFooterCellDef></td>
    </ng-container>

    <!-- Selection column -->
    <ng-container matColumnDef="select">
      <th mat-header-cell *matHeaderCellDef>
        <div class="no-truncate d-flex">
          <mat-checkbox
            data-testId="select-all-rows"
            color="primary"
            (change)="selection.isAllSelected() ? selection.clear(): selection.selectAll()"
            [checked]="selection.hasValue() && selection.isAllSelected()"
            [indeterminate]="selection.hasValue() && !selection.isAllSelected()"
            [disabled]="!(options?.selectionConfig?.allowSelection ?? true)"
          ></mat-checkbox>
        </div>
      </th>
      <td mat-cell *matCellDef="let data; index as rowIndex">
        <div class="no-truncate d-flex">
          <mat-checkbox
            color="primary"
            (click)="$event.stopPropagation()"
            (change)="selection.toggle(data)"
            [attr.data-testId]="'select-row-' + (rowIndex+1)"
            [checked]="selection.isSelected(data)"
            [disabled]="!(options?.selectionConfig?.allowSelection ?? true) || !(data | anyFnExec : options?.rowConfig?.allowSelection : rowsRevision)"
          ></mat-checkbox>
        </div>
      </td>

      <td
        class="align-left white-space-pre-wrap"
        [attr.colspan]="options?.editConfig?.allowEditing ? 2 : 1"
        [columnDynamicWidth]="{
        isEnabled: options?.editConfig?.allowEditing && options?.sequenceConfig?.dynamicWidth,
        columnDef: 'sequence',
        minWidth: 30,
        maxWidth: options?.sequenceConfig?.maxWidth ?? 300,
        externalPadding: defaultColumns?.select?.definition?.width
        }"
        mat-footer-cell
        *matFooterCellDef
      >
        <span>
          {{ options?.editConfig?.allowEditing && options.footerConfig.displayFooter ? (options?.footerConfig.label ??
          '') : ''}}
        </span>
      </td>
    </ng-container>

    <!-- Sequence column -->
    <ng-container matColumnDef="sequence">
      <th mat-header-cell *matHeaderCellDef class="align-left">#</th>
      <td mat-cell class="qa-grid-column-sequence" *matCellDef="let element; index as rowIndex" class="align-left">
        {{ element.customIndex ?? (rowIndex + 1)}}
      </td>

      <td
        class="align-left white-space-pre-wrap"
        [columnDynamicWidth]="{
          isEnabled:!options?.editConfig?.allowEditing && options?.sequenceConfig?.dynamicWidth,
          columnDef: 'sequence',
          minWidth: 30,
          maxWidth: options?.sequenceConfig?.maxWidth ?? 300
        }"
        [hidden]="options?.editConfig?.allowEditing"
        mat-footer-cell
        *matFooterCellDef
      >
        <span>{{ (options.footerConfig.displayFooter ? options?.footerConfig.label : '') ?? ''}}</span>
      </td>
    </ng-container>

    <!-- Group header -->
    <ng-container matColumnDef="groupHeader">
      <td [attr.colspan]="displayedColumns?.length ?? 0" mat-cell *matCellDef="let group">
        <ng-container>
          <div class="d-flex align-items-center" [attr.data-testId]="group.name">
            <mat-icon
              class="flex-shrink-0 mr-3"
              [svgIcon]="(collapsedGroups | arrayIncludes: group.value) ? 'chevron-down': 'chevron-up'"
            ></mat-icon>
            <b>
              {{ group?.name }}
              <span *ngIf="!group.isActive">{{ group.inactiveLabel ?? '(Inactive)'}}</span>
            </b>
          </div>
        </ng-container>
      </td>
    </ng-container>

    <!-- Row grouping-->

    <ng-container matColumnDef="rowGroup">
      <th
        mat-header-cell
        *matHeaderCellDef="let data"
        [class]="options.rowGroupConfig.customHeaderClass"
        [attr.data-testId]="data?.header"
      >
        {{data?.header ?? ''}}
      </th>
      <td
        mat-cell
        *matCellDef="let data; let i = index"
        [attr.rowspan]="rowGroupsCellSpans[i]"
        [style.display]="rowGroupsCellSpans[i] ? '' : 'none'"
      >
        <div
          *ngLet="(data[options.rowGroupConfig.columnDef] | anyFnExec : options.rowGroupConfig.getGroupName) as groupName"
          [tippy]="groupName"
          [onlyTextOverflow]="true"
          variation="tooltip"
          [lazy]="true"
          [delay]="[100, 0]"
          [duration]="[100, 0]"
          [trigger]="'mouseenter'"
          [placement]="'bottom'"
        >
          {{groupName}}
        </div>
      </td>
    </ng-container>

    <ng-container *ngFor="let column of columns; trackBy: trackByColumn" [matColumnDef]="column.columnDef">
      <th
        *matHeaderCellDef
        mat-header-cell
        mat-sort-header
        #headerCellElement
        [disabled]="!(column.allowSorting ?? true)"
        [style.min-width]="column.width ? (column.width | cssPixelValue) : (column.minWidth | cssPixelValue)"
        [style.width]="column.width ? (column.width | cssPixelValue) : null"
        [class]="((data | anyFnExec:column.header?.alignment) ?? GridAlignment.Left) + ' ' + (column.header?.customClass ?? '')"
      >
        <!-- The overflow check runs on the name span, because next to an icon the outer span is a flex box that never overflows. -->
        <span
          [attr.data-testId]="'column-header-' + column.header?.name"
          *ngIf="column.header?.name && !column.header?.useTemplate"
          #tooltip="tippy"
          [tippy]="column.header?.name"
          [tippyHost]="headerName"
          variation="tooltip"
          [onlyTextOverflow]="true"
          [lazy]="true"
          [delay]="[100, 0]"
          [duration]="[100, 0]"
          [trigger]="'mouseenter'"
          [placement]="'top'"
          [triggerTarget]="$any(headerCellElement).nativeElement"
          [class.d-flex]="column.header?.icon"
          [class.align-items-center]="column.header?.icon"
        >
          <mat-icon
            *ngIf="column.header?.icon"
            class="columnHeaderIcon"
            [svgIcon]="column.header.icon"
            [tippy]="column.header.tooltip"
            variation="tooltip"
            [lazy]="true"
            [delay]="[100, 0]"
            [duration]="[100, 0]"
            [placement]="'top'"
          ></mat-icon>
          <span #headerName>{{column.header?.name}}</span>
        </span>
        <ng-container
          *ngIf="column.header?.template && column.header?.useTemplate"
          [ngTemplateOutlet]="column.header?.template"
          [ngTemplateOutletContext]="{$implicit: column.header}"
        ></ng-container>
        <ng-container *ngIf="options?.sortConfig?.mode !== GridSortMode.ColumnHeader && sortingDefinition?.length">
          <mat-icon
            *ngIf="(sortingDefinition | findByProperty : 'columnDef' : column.columnDef) as definition"
            class="columnHeaderSortArrow"
            [svgIcon]="definition?.direction === GridSortDirection.Ascending ? 'north' : 'south'"
          ></mat-icon>
        </ng-container>
      </th>
      <td
        mat-cell
        *matCellDef="let data; index as rowIndex"
        #cell
        #tooltip="tippy"
        [attr.data-testId]="column.header?.name + '-' + (rowIndex+1)"
        [tippy]="TooltipMessageComponent"
        variation="tooltip"
        [data]="{ data, column, options }"
        [onlyTextOverflow]="data | anyFnExec: (column.tooltip?.onlyTextOverflow ?? true)"
        [lazy]="true"
        [delay]="[100, 0]"
        [duration]="[100, 0]"
        [hideOnClick]="column.tooltip?.hideOnClick"
        [trigger]="column.tooltip?.trigger ?? 'mouseenter'"
        [placement]="$any(column.tooltip?.position ?? 'bottom')"
        [isEnabled]="!(data[column.tooltip?.property ?? column.columnDef] | tooltipDisabled : data : column : options : rowsRevision)"
        [tabindex]="(data | anyFnExec:column.tabIndex:rowsRevision) ?? 0"
        [style.min-width]="column.width ? (column.width | cssPixelValue) : (column.minWidth | cssPixelValue)"
        [style.width]="column.width ? (column.width | cssPixelValue) : null"
        [ngClass]="{
          'dirty': !options?.changesConfig?.disableHighlighting && !column.disableChangeHighlighting && changes.isPropertyChanged(data, $any(column.columnDef)),
          'focused' : !(data | anyFnExec:column.disableFocusedBackground:rowsRevision),
          'hovered' : (options?.editConfig?.allowEditing && !(data | anyFnExec:column.disableFocusedBackground:rowsRevision))
        }"
        [class]="((data | anyFnExec:column.columnAlignment) ?? GridAlignment.Left) +' qa-grid-' + column.columnDef +'-column'"
      >
        <ng-template
          *gridCellDef="{
              column, data,rowIndex: rowIndex + 1,
              row: {
                actions: {
                  delete: onDeleteRow.bind(this, data, cell)
                },
                allowDeleting: options?.rowConfig?.allowDeleting
              },
              cell: {
                element: cell,
                actions: { onEdited: onCellEdited.bind(this, data, column.columnDef, null, data[column.columnDef]) }
              },
              allowEditing: options?.editConfig?.allowEditing,
              allowDeleting: options?.deleteConfig?.allowDeleting,
              tooltip
            }"
        ></ng-template>
      </td>
      <ng-container *ngIf="options?.footerConfig">
        <td
          mat-footer-cell
          *matFooterCellDef
          [style.min-width]="column.width ? (column.width | cssPixelValue) : (column.minWidth | cssPixelValue)"
          [style.width]="column.width ? (column.width | cssPixelValue) : null"
          [class]="column.header?.alignment ?? GridAlignment.Left"
        >
          <ng-container *ngLet="(shouldRecomputeFooter$ | async) as shouldRecomputeFooter">
            <ng-container
              [ngSwitch]="column.footer?.type"
              *ngLet="(shouldRecomputeFooter?.column === column.columnDef ? shouldRecomputeFooter.timestamp : null) as timestamp"
            >
              <!-- let temp val, match by colDef, pass timestamp to pipe -->
              <span *ngSwitchCase="GridFooterType.Text">{{column.footer?.label}}</span>
              <span *ngSwitchCase="GridFooterType.Sum">
                {{data | sum: column.columnDef : timestamp : column.footer?.format | number :
                column.footer?.format}}{{column.footer?.allowPercentage ? '%' : '' }}
              </span>
            </ng-container>
          </ng-container>
        </td>
      </ng-container>
    </ng-container>

    <!-- Stacked headers -->
    <ng-container *ngIf="stackedHeaderRows?.length">
      <ng-container *ngFor="let row of stackedHeaderRows; let i = index; trackBy: trackByIndex ">
        <tr mat-header-row *matHeaderRowDef="(row.stackedHeaderColumns | mapByProperty : 'columnDef')"></tr>

        <ng-container *ngFor="let column of row.stackedHeaderColumns">
          <ng-container [matColumnDef]="column.columnDef">
            <th
              mat-header-cell
              *matHeaderCellDef
              [class]="(column.alignment ?? GridAlignment.Left) + ' ' + (column.customHeaderClass ?? '')"
              [attr.colspan]="column.colspan"
              [attr.data-testId]="column.headerText"
            >
              {{column.headerText}}
            </th>
          </ng-container>
        </ng-container>
      </ng-container>
    </ng-container>

    <tr id="displayedColumnsHeaderRow" mat-header-row *matHeaderRowDef="displayedColumns"></tr>

    <ng-container *ngIf="filterControls?.columns?.length">
      <tr mat-header-row *matHeaderRowDef="filteredColumns"></tr>

      <ng-container *ngFor="let columnDef of filteredColumns">
        <ng-container
          *ngLet="(filterControls.columns | findByProperty : 'columnDef': columnDef) as column"
          [matColumnDef]="columnDef"
        >
          <th #filterCell mat-header-cell *matHeaderCellDef [ngClass]="{'filter-header-cell' : column }">
            <ng-container *ngIf="column">
              <ng-template
                *filterCellDef="{column, cell: {element: filterCell}, placeholder: filterControls.placeholder}"
              ></ng-template>
            </ng-container>
          </th>
        </ng-container>
      </ng-container>
    </ng-container>

    <tr
      mat-row
      *matRowDef="let data; columns: displayedColumns"
      [ngClass]="{'dirty': !options?.changesConfig?.disableHighlighting && changes.isPropertyChanged(data), 'collapsedGroup': (collapsedGroups | arrayIncludes : data[groupByColumn])}"
      class="qa-grid-row"
      cdkDrag
      cdkDragLockAxis="y"
      cdkDragBoundary=".dragBoundary"
      [cdkDragData]="data"
      [id]="data.id"
      (click)="onRowClicked(data)"
    ></tr>
    <ng-container *ngIf="options.groupConfig">
      <tr
        mat-row
        [hidden]="!(group.value | groupHasData : groupByColumn : data )"
        *matRowDef="let group; columns: ['groupHeader']; when: isGroup"
        class="cursor-pointer position-sticky groupRowHeader"
        (click)="onToggleExpandedGroup(group)"
        cdkDrag
        cdkDragDisabled="true"
        cdkDragLockAxis="y"
        cdkDragBoundary=".dragBoundary"
        [cdkDragData]="group"
      ></tr>
    </ng-container>

    <ng-container *ngIf="options?.footerConfig">
      <tr
        [hidden]="!(dataSource.data?.length > 0) || !options?.footerConfig.displayFooter"
        mat-footer-row
        *matFooterRowDef="displayedColumns"
      ></tr>
    </ng-container>

    <!-- Row shown when there is no matching data that will be provided to the wrapper table -->
    <tr class="mat-row noDataRow" *matNoDataRow>
      <td class="mat-cell no-truncate py-3" [attr.colspan]="displayedColumns?.length ?? 0">
        {{options?.labeling?.noRowsMessage ?? 'No records to display'}}
      </td>
    </tr>
  </table>
</div>

<ng-template #headerAddButtonTemplate>
  <button
    [tippy]="options.labeling.addShortcutTooltip ?? 'Add Entry (Shift and +)'"
    [lazy]="true"
    [delay]="[100, 0]"
    [duration]="[100, 0]"
    [attr.data-testId]="options?.labeling?.addButton ?? 'Add Entry'"
    class="qa-grid-header-add-btn"
    mat-flat-button
    color="secondary"
    (click)="onAddRow()"
  >
    <mat-icon [svgIcon]="options?.icons?.addButton?.header ?? 'add'"></mat-icon>
    {{options?.labeling?.addButton ?? 'Add Entry'}}
  </button>
</ng-template>

<ng-template #floatingAddButtonTemplate>
  <!-- TODO(Grid) .main-container should not be part of grid (extract in app default config) -->
  <button
    data-testId="add-entry"
    [tippy]="options.labeling.addShortcutTooltip ?? 'Add Entry (Shift and +)'"
    [lazy]="true"
    [delay]="[100, 0]"
    [duration]="[100, 0]"
    mat-flat-button
    class="floatingAddButton qa-grid-floating-add-btn"
    color="primary"
    appMorphingButton
    [isEnabled]="options?.addConfig?.morphingButtonConfig?.enabled ?? true"
    [scrollableSelector]="options?.addConfig?.morphingButtonConfig?.scrollableSelector ?? '.main-container'"
    (click)="onAddRow()"
  >
    <mat-icon [svgIcon]="options?.icons?.addButton?.floating ?? 'add'"></mat-icon>
    <span class="label">{{ options?.labeling?.addButton ?? 'Add Entry' }}</span>
  </button>
</ng-template>

<ng-template #sortSelectionTemplate>
  <ng-container *ngIf="selectedSortOption">
    <button
      mat-icon-button
      color="primary"
      data-testId="sort-direction"
      class="tableHeaderSortDirectionButton"
      [matTooltip]="selectedSortOption.direction === GridSortDirection.Ascending ?
        options?.labeling?.sortDescending ?? 'Sort descending' : options?.labeling?.sortAscending ?? 'Sort ascending'"
      (click)="onToggleSortDirection()"
    >
      <mat-icon
        [svgIcon]="selectedSortOption.direction === GridSortDirection.Ascending ? 'sort-descending' : 'sort-ascending'"
      ></mat-icon>
    </button>

    <button mat-button color="primary" data-testId="sort-options-trigger" [matMenuTriggerFor]="sortOptions">
      <span class="tableHeaderSortDropdownWrapper">
        <span class="fixTableHeaderSortText">
          {{options?.labeling?.tableHeaderSortBy ?? 'Sort by'}}&nbsp;{{(options?.sortConfig?.selectionOptions |
          findByProperty: 'columnDef': selectedSortOption.columnDef)?.displayName}}
        </span>
        <mat-icon class="tableHeaderSortDropdownArrow" svgIcon="menu-down"></mat-icon>
      </span>

      <mat-menu #sortOptions="matMenu">
        <div *ngFor="let option of options?.sortConfig?.selectionOptions">
          <button
            mat-menu-item
            [attr.data-testId]="option.displayName"
            [ngClass]="{'selectedMenuItem': option.columnDef === selectedSortOption.columnDef}"
            (click)="onSortColumnChanged(option.columnDef)"
          >
            <span>{{option.displayName}}</span>
          </button>
        </div>
      </mat-menu>
    </button>
  </ng-container>
</ng-template>

<ng-template #copyRowsTemplate>
  <ng-container *ngIf="selection.selected.length as selectedRowsCount">
    <button
      mat-flat-button
      color="primary"
      class="mat-secondary"
      data-testId="grid-bulk-copy"
      (click)="options?.copyConfig?.onCopySelectedRows()"
    >
      <mat-icon [svgIcon]="options?.icons?.copyButton ?? 'content-copy'"></mat-icon>
      <span>{{options?.labeling?.copyButton ?? 'Copy'}} ({{selectedRowsCount}})</span>
    </button>
  </ng-container>
</ng-template>
